import React, { useState, useRef, useEffect, useMemo } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { Modal, Button, Upload, Input, Select, Progress, message } from 'antd';
import {
  UploadOutlined,
  InboxOutlined,
  CheckCircleOutlined,
  FolderOpenOutlined,
  FileAddOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import JSZip from 'jszip';
import { UPLOAD_BASE_URL } from '../../utils/constants/config';

const { Dragger } = Upload;
const { Option } = Select;

const teal = '#2a5f5a';
const tealHover = '#245249';

const modeCardStyle = (selected) => ({
  flex: 1,
  padding: 16,
  borderRadius: 8,
  border: selected ? `2px solid ${teal}` : '2px solid #e5e7eb',
  background: selected ? 'rgba(42,95,90,0.04)' : '#f9fafb',
  cursor: 'pointer',
  textAlign: 'left',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  appearance: 'none',
});

const radioCircleStyle = (selected) => ({
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: `2px solid ${selected ? teal : '#d1d5db'}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginTop: 2,
});

const radioInnerStyle = {
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: teal,
};

const fieldLabelStyle = { display: 'block', fontWeight: 500, marginBottom: 4, color: '#111827' };

const formatBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatSpeed = (bps) => {
  if (!bps || bps <= 0) return '\u2014';
  if (bps < 1024) return `${Math.round(bps)} B/s`;
  if (bps < 1024 * 1024) return `${(bps / 1024).toFixed(0)} KB/s`;
  return `${(bps / (1024 * 1024)).toFixed(2)} MB/s`;
};

const formatDuration = (sec) => {
  if (!sec || sec < 1) return '<1s';
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}m ${s}s`;
};

// ── Bundling ──────────────────────────────────────────────────────────────
// One HTTP request per instance makes a 2,000-slice CT pay 2,000 round trips
// to move ~600KB each.  Packing instances into a ZIP turns that into ~60
// requests AND shrinks the bytes: measured on a real CT from an outside
// facility, an uncompressed 515KB slice deflates to 219KB (2.35x).  Orthanc
// accepts a ZIP at POST /instances and unpacks it itself (verified against
// production Orthanc 1.12.10, with DEFLATE compression).
//
// This only helps sites whose UPLOAD is slower than the server's ingest
// ceiling (~18 slices/sec, set by JPEG 2000 transcoding on arrival — ~107ms of
// CPU per slice).  That is most Ethiopian hospitals at 10-50 Mb/s.  On a fast
// link the server is already the wall and this changes nothing, so do not
// judge it from a fast connection.
//
// 24MB of source DICOM compresses to ~10MB, which at 10 Mb/s is ~65s on the
// wire — comfortably inside nginx's 300s proxy_read_timeout on this path.
// Larger bundles would risk that timeout on exactly the slow links this helps.
const BUNDLE_TARGET_BYTES = 24 * 1024 * 1024;
const BUNDLE_MAX_FILES = 300;
// Fewer, far larger requests need less parallelism to keep the pipe full, and
// every in-flight bundle costs memory (source + compressed copy) on a hospital
// PC.  Four keeps peak use near 140MB even with worst-case bundles.
const BUNDLE_CONCURRENCY = 4;
// Below this, bundling buys nothing and the proven per-file path is kept: an
// X-ray tech sending one or two images should not exercise new machinery.
const BUNDLE_MIN_FILES = 9;

// Groups file indices into bundles capped by BOTH bytes and count — a folder
// of tiny X-rays would otherwise put thousands of entries in one archive.
const buildBundles = (files) => {
  const bundles = [];
  let cur = { idxs: [], bytes: 0 };
  files.forEach((f, i) => {
    const size = (f && f.size) || 0;
    if (
      cur.idxs.length > 0 &&
      (cur.bytes + size > BUNDLE_TARGET_BYTES || cur.idxs.length >= BUNDLE_MAX_FILES)
    ) {
      bundles.push(cur);
      cur = { idxs: [], bytes: 0 };
    }
    cur.idxs.push(i);
    cur.bytes += size;
  });
  if (cur.idxs.length > 0) bundles.push(cur);
  return bundles;
};

// Entry names are irrelevant to Orthanc — it reads the DICOM header, not the
// filename — but they must be unique within the archive.
const zipFiles = (files, compress) =>
  new Promise((resolve, reject) => {
    const zip = new JSZip();
    files.forEach((f, i) => {
      zip.file(`${String(i).padStart(5, '0')}.dcm`, f);
    });
    zip
      .generateAsync({
        type: 'blob',
        compression: compress ? 'DEFLATE' : 'STORE',
        // Level 9 measured 217KB vs level 6's 219KB on a real CT slice — no
        // gain worth the extra CPU on a hospital PC.
        compressionOptions: { level: 6 },
      })
      .then(resolve, reject);
  });

// A patient folder straight off a scanner or a CD is never just images: it
// also carries a DICOMDIR index, a bundled viewer, thumbnails and OS cruft.
// Anything with one of these extensions is dropped without even being probed.
const NON_DICOM_EXT = new Set([
  'txt', 'xml', 'html', 'htm', 'pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'tif', 'tiff',
  'zip', 'rar', '7z', 'gz', 'tar', 'exe', 'dll', 'bat', 'sh', 'ini', 'inf', 'cfg', 'db',
  'js', 'css', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'mp4', 'avi', 'log', 'json',
]);

const extensionOf = (name) => {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase();
};

// Read the 4 bytes at the DICOM magic offset. Blob.arrayBuffer() is absent on
// older Edge/Safari, and this dialog runs on whatever browser a hospital PC
// happens to have, so fall back to FileReader rather than misidentify every
// file on a machine that lacks it.
const readHead = (file) => {
  const slice = file.slice(128, 132);
  if (typeof slice.arrayBuffer === 'function') return slice.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(slice);
  });
};

// Part 10 DICOM carries the literal string "DICM" at byte offset 128, right
// after the preamble. Probing those four bytes is the only dependable way to
// recognise a folder's contents: real CT/MRI exports are overwhelmingly
// *extensionless* (IM_0001, 1.2.840.113619...) or Siemens .IMA, so filtering
// on ".dcm" would throw away almost everything a radiologist selects.
// file.slice() is lazy — this reads 4 bytes, not the whole 500KB slice.
const looksLikeDicom = async (file) => {
  const name = file.name || '';
  if (name.toUpperCase() === 'DICOMDIR') return false;
  if (name === '.DS_Store' || name === 'Thumbs.db') return false;
  if (!file.size) return false; // dropped directories surface as 0-byte Files
  const ext = extensionOf(name);
  if (NON_DICOM_EXT.has(ext)) return false;
  try {
    const head = await readHead(file);
    if (head && String.fromCharCode.apply(null, new Uint8Array(head)) === 'DICM') return true;
  } catch (e) {
    // Unreadable slice (permissions, vanished file) — fall through to the name.
  }
  // A few modalities write a raw dataset with no preamble, so absence of the
  // magic bytes is not proof. Keep those only when the name is plausible;
  // Orthanc rejects anything we still get wrong.
  return ext === '' || ext === 'dcm' || ext === 'dicom' || ext === 'ima';
};

// Probe the batch with a worker pool — 500 sequential awaits would take a
// noticeable moment on a spinning disk or a network share.
const filterDicomFiles = async (files) => {
  const kept = [];
  let next = 0;
  const PROBE_CONCURRENCY = 32;
  const workers = Array.from({ length: Math.min(PROBE_CONCURRENCY, files.length) }, async () => {
    for (;;) {
      const idx = next++;
      if (idx >= files.length) return;
      if (await looksLikeDicom(files[idx])) kept.push(files[idx]);
    }
  });
  await Promise.all(workers);
  return kept;
};

// Identity for de-duplication: the same folder selected twice, or a folder
// dropped on top of one already staged, must not upload everything again.
const fileKey = (f) =>
  `${f.webkitRelativePath || ''}|${f.name}|${f.size}|${f.lastModified}`;

// rc-upload fills in webkitRelativePath ("PatientA/CT/IM_0001") for both the
// folder picker and folder drops, which gives us the patient folder name.
const folderOf = (f) => {
  const path = f.webkitRelativePath || '';
  const slash = path.indexOf('/');
  return slash === -1 ? '' : path.slice(0, slash);
};

export function UploadStudyDialog({ open, onClose, onSuccess }) {
  const [mode, setMode] = useState('dicom-only');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileList, setFileList] = useState([]);
  // Human-visible upload telemetry — answers the perennial "is the upload
  // hung or just on a slow link?" question by showing live MB/s + ETA.
  const [uploadStats, setUploadStats] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [skippedCount, setSkippedCount] = useState(0);
  // Files finished, shown as "142 of 500". A byte percentage alone stalls
  // visibly on a big slice; a file counter always has something to say.
  const [filesDone, setFilesDone] = useState(0);

  // Files stream in one at a time when a folder is dropped (rc-upload's tree
  // walk invokes beforeUpload per file), so they are parked in a ref and
  // flushed to state on a timer. Appending 500 times with setState would be
  // O(n^2) copying plus 500 renders — enough to lock up the tab on React 17.
  const pendingRef = useRef([]);
  const flushTimerRef = useRef(null);

  const flushPending = async () => {
    flushTimerRef.current = null;
    const batch = pendingRef.current;
    pendingRef.current = [];
    if (!batch.length) return;

    setScanning(true);
    try {
      // Manual entry may legitimately carry non-DICOM images, so only the
      // DICOM-only path gets magic-byte filtering.
      const kept = mode === 'dicom-only' ? await filterDicomFiles(batch) : batch;
      const dropped = batch.length - kept.length;
      if (dropped > 0) setSkippedCount((n) => n + dropped);

      setFileList((prev) => {
        const seen = new Set(prev.map(fileKey));
        const merged = prev.slice();
        kept.forEach((file) => {
          const key = fileKey(file);
          if (seen.has(key)) return;
          seen.add(key);
          // antd keys its controlled list by uid; raw File objects have none.
          if (!file.uid) file.uid = `afya-${key}`;
          merged.push(file);
        });
        return merged;
      });
    } finally {
      setScanning(false);
    }
  };

  const queueFile = (file) => {
    pendingRef.current.push(file);
    // Fixed 150ms cadence rather than a resetting debounce: a folder walk can
    // take seconds, and the count should climb while it runs.
    if (!flushTimerRef.current) {
      flushTimerRef.current = setTimeout(flushPending, 150);
    }
  };

  useEffect(() => () => clearTimeout(flushTimerRef.current), []);

  // Closing the tab mid-batch leaves a study on the server missing slices,
  // which is worse than no study at all — make the browser ask first.
  useEffect(() => {
    if (!uploading) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [uploading]);

  // Per-patient-folder breakdown shown instead of antd's file list: 500 list
  // rows are unusable and slow, while "PatientA — 500 files" is the actual
  // confirmation the radiologist needs before committing the upload.
  const summary = useMemo(() => {
    const byFolder = new Map();
    let totalBytes = 0;
    fileList.forEach((f) => {
      const name = folderOf(f) || 'Individual files';
      const entry = byFolder.get(name) || { name, count: 0, bytes: 0 };
      entry.count += 1;
      entry.bytes += f.size || 0;
      byFolder.set(name, entry);
      totalBytes += f.size || 0;
    });
    return {
      totalBytes,
      folders: Array.from(byFolder.values()).sort((a, b) => b.count - a.count),
    };
  }, [fileList]);

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('Please select files to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStats(null);
    setFilesDone(0);
    const startTime = performance.now();

    // Concurrency limit: how many files upload in parallel. Browsers cap
    // concurrent connections per origin at ~6, and Orthanc/Postgres digest
    // each instance synchronously, so 6 is a safe sweet spot — past that we
    // start queueing inside the browser without any speedup.
    const CONCURRENCY = 6;

    try {
      let successCount = 0;
      let failCount = 0;
      let completed = 0;
      const totalFiles = fileList.length;

      // Track per-file upload progress (bytes) so the overall progress bar
      // moves smoothly even for a single large DICOM, rather than staying at
      // 0% until that file completes and then jumping.
      const perFileLoaded = new Array(totalFiles).fill(0);
      const perFileTotal = fileList.map((it) => (it.originFileObj || it).size || 0);
      const totalBytes = perFileTotal.reduce((a, b) => a + b, 0) || 1;

      // Running total, updated by delta. The previous code re-reduced the
      // whole per-file array on every progress event; at 500 files that is a
      // 500-element scan several hundred times a second.
      let loadedBytes = 0;
      const setFileLoaded = (idx, value) => {
        loadedBytes += value - perFileLoaded[idx];
        perFileLoaded[idx] = value;
      };

      // Repaint at most every PAINT_MS. axios fires onUploadProgress per
      // chunk, so with 6 workers this callback runs hundreds of times a
      // second; React 17 does NOT batch setState from async callbacks, so
      // painting on every event meant hundreds of renders/sec and a modal
      // that froze exactly while the radiologist was watching it. Throttling
      // costs nothing visually — the eye cannot read a bar faster than this.
      const PAINT_MS = 100;
      let lastPaint = 0;
      let paintTimer = null;
      let finishing = false;

      const paint = () => {
        lastPaint = performance.now();
        // Cap at 99% until all files have a 200/201 response so the user
        // doesn't see 100% before Orthanc actually persists the last byte.
        const pct = Math.min(99, Math.round((loadedBytes / totalBytes) * 100));
        // React 17: one batched commit instead of two separate renders.
        unstable_batchedUpdates(() => {
          setUploadProgress(completed === totalFiles && !finishing ? 100 : pct);
          setFilesDone(completed);
          const elapsedSec = (performance.now() - startTime) / 1000;
          if (elapsedSec > 0.25) {
            const speedBps = loadedBytes / elapsedSec; // bytes/sec
            const remainingBytes = Math.max(0, totalBytes - loadedBytes);
            const etaSec = speedBps > 0 ? remainingBytes / speedBps : 0;
            setUploadStats({ loaded: loadedBytes, totalBytes, speedBps, etaSec });
          }
        });
      };

      // force=true is used at the end of each file so the final numbers are
      // exact rather than whatever the throttle last happened to show.
      const refreshProgress = (force) => {
        const since = performance.now() - lastPaint;
        if (force || since >= PAINT_MS) {
          if (paintTimer) {
            clearTimeout(paintTimer);
            paintTimer = null;
          }
          paint();
        } else if (!paintTimer) {
          // Trailing edge: guarantees the bar lands on the true final value
          // even if the last event arrives inside the throttle window.
          paintTimer = setTimeout(() => {
            paintTimer = null;
            paint();
          }, PAINT_MS - since);
        }
      };

      const uploadOne = async (item, idx, isRetry) => {
        const file = item.originFileObj || item;
        try {
          // Pass the File/Blob directly — axios + browser will stream the
          // body without first reading the whole file into JS heap memory.
          // Avoiding `await file.arrayBuffer()` is critical for large DICOMs
          // (CT/MRI multi-frame can be 50–200MB); the previous code blocked
          // for the entire file read before a single byte hit the network.
          //
          // URL is built from UPLOAD_BASE_URL (port 8443, HTTP/1.1) instead
          // of a same-origin path so the request bypasses the main HTTP/2
          // listener whose per-stream window throttles single uploads to
          // ~430 KB/s on transatlantic links.  See nginx.conf for details.
          const response = await axios.post(`${UPLOAD_BASE_URL}/orthanc/instances`, file, {
            headers: { 'Content-Type': 'application/dicom' },
            // Disable axios' default 10s body timeout for large uploads on
            // slow networks — nginx already enforces a 300s server timeout.
            timeout: 0,
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
            onUploadProgress: (e) => {
              // A retry restarts this file at 0, so the delta goes negative
              // and correctly un-counts the bytes from the failed attempt.
              setFileLoaded(idx, e.loaded || 0);
              refreshProgress();
            },
          });
          if (response.status === 200 || response.status === 201) {
            successCount++;
            setFileLoaded(idx, perFileTotal[idx]);
            return true;
          }
          return false;
        } catch (error) {
          console.error('Upload error for file:', file.name, error);
          return false;
        } finally {
          // A retried file was already counted towards completion on its first
          // attempt; counting it twice would push the bar past 100%.
          if (!isRetry) completed++;
          refreshProgress(true);
        }
      };

      // ── Bundled path ──────────────────────────────────────────────────
      // Files are sent as compressed ZIP bundles when there are enough of them
      // to be worth it. Progress is accounted in SOURCE bytes (what the
      // radiologist sees in their folder), not compressed bytes, so the MB/s
      // and ETA describe the job rather than the wire.
      const bundles = totalFiles > BUNDLE_MIN_FILES ? buildBundles(fileList) : null;
      const bundleLoaded = bundles ? new Array(bundles.length).fill(0) : null;
      const setBundleLoaded = (b, value) => {
        loadedBytes += value - bundleLoaded[b];
        bundleLoaded[b] = value;
      };
      // Decided once from real data: DICOM off a CD is uncompressed and
      // deflates ~2.35x, but a source already in JPEG2000/JPEG-LS gains
      // nothing and the CPU would be wasted on a hospital PC.
      let compressBundles = true;

      const uploadBundle = async (bIdx, isRetry) => {
        const bundle = bundles[bIdx];
        const files = bundle.idxs.map((i) => fileList[i].originFileObj || fileList[i]);
        try {
          const blob = await zipFiles(files, compressBundles);
          if (bIdx === 0 && !isRetry && bundle.bytes > 0) {
            const ratio = bundle.bytes / Math.max(blob.size, 1);
            if (ratio < 1.1) {
              compressBundles = false;
              console.warn(
                `[Upload] source compresses only ${ratio.toFixed(2)}x — already compressed; sending the rest without deflate`
              );
            }
          }
          const response = await axios.post(`${UPLOAD_BASE_URL}/orthanc/instances`, blob, {
            headers: { 'Content-Type': 'application/zip' },
            timeout: 0,
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
            onUploadProgress: (e) => {
              // e.total is the COMPRESSED size; scale to source bytes so the
              // bar advances in the units the file count is expressed in.
              const frac = e.total ? Math.min(1, (e.loaded || 0) / e.total) : 0;
              setBundleLoaded(bIdx, frac * bundle.bytes);
              refreshProgress();
            },
          });
          // Orthanc answers with one entry per stored instance. Fewer entries
          // than files means something in the archive was rejected, and we
          // cannot tell which — fall back so a single bad slice costs one file
          // rather than the whole bundle.
          const stored = Array.isArray(response.data) ? response.data.length : 0;
          const ok = (response.status === 200 || response.status === 201) && stored >= files.length;
          if (ok) {
            successCount += files.length;
            setBundleLoaded(bIdx, bundle.bytes);
            return true;
          }
          console.warn(`[Upload] bundle ${bIdx}: Orthanc stored ${stored} of ${files.length}`);
          setBundleLoaded(bIdx, 0);
          return false;
        } catch (error) {
          console.error(`[Upload] bundle ${bIdx} failed:`, error);
          // Drop this bundle's contribution before any per-file fallback
          // re-counts the same bytes.
          setBundleLoaded(bIdx, 0);
          return false;
        } finally {
          if (!isRetry) completed += files.length;
          refreshProgress(true);
        }
      };

      // Simple worker-pool: run CONCURRENCY workers that pull from a shared
      // queue index. This is faster than chunking with Promise.all because no
      // worker is ever idle waiting for a slow file in the same batch.
      let nextIndex = 0;
      const failedIdx = [];
      const failedBundles = [];
      const unit = bundles ? bundles.length : totalFiles;
      const poolSize = bundles
        ? Math.min(BUNDLE_CONCURRENCY, unit)
        : Math.min(CONCURRENCY, unit);
      const workers = Array.from({ length: poolSize }, async () => {
        while (true) {
          const myIdx = nextIndex++;
          if (myIdx >= unit) return;
          if (bundles) {
            const ok = await uploadBundle(myIdx);
            if (!ok) failedBundles.push(myIdx);
          } else {
            const ok = await uploadOne(fileList[myIdx], myIdx);
            if (!ok) failedIdx.push(myIdx);
          }
        }
      });
      await Promise.all(workers);

      // A failed bundle gets one more try as a unit; if it fails again its
      // files go one by one, so a single unreadable instance cannot take 300
      // good ones with it.
      if (failedBundles.length > 0) {
        finishing = true;
        refreshProgress(true);
        for (const b of failedBundles) {
          console.warn(`[Upload] retrying bundle ${b} (${bundles[b].idxs.length} files)`);
          if (await uploadBundle(b, true)) continue;
          console.warn(`[Upload] bundle ${b} failed twice — falling back to per-file`);
          for (const idx of bundles[b].idxs) {
            if (!(await uploadOne(fileList[idx], idx, true))) failedIdx.push(idx);
          }
        }
        finishing = false;
      }

      // One automatic retry pass. Over a 500-instance folder on a hospital
      // link a handful of transient network/5xx failures is normal, and a
      // study that lands missing slices is a clinical hazard rather than an
      // inconvenience. Retried serially so a congested link can recover.
      //
      // Bundled uploads skip this: anything in failedIdx there has ALREADY had
      // its own per-file attempt during the bundle fallback above, so retrying
      // here would be a third try and would double-count the failures.
      if (bundles) {
        failCount = failedIdx.length;
      } else if (failedIdx.length > 0) {
        console.warn(`[Upload] retrying ${failedIdx.length} failed file(s)`);
        // Hold the bar off 100% while retries are still in flight, so it never
        // reads "done" on an upload that is still working.
        finishing = true;
        refreshProgress(true);
        for (const idx of failedIdx) {
          const ok = await uploadOne(fileList[idx], idx, true);
          if (!ok) failCount++;
        }
        finishing = false;
      }

      // Land the bar on its true final value; the throttle may still be
      // holding an intermediate frame in a pending timer.
      refreshProgress(true);

      if (failCount === 0) {
        const successMessage =
          mode === 'dicom-only'
            ? 'DICOM files uploaded successfully! Patient data extracted automatically.'
            : 'Study uploaded successfully with manual entry.';
        message.success(`${successMessage} (${successCount} file${successCount > 1 ? 's' : ''})`);
        // Fire refresh immediately — Orthanc stores synchronously so the study is
        // already indexed by the time we reach here. Close the modal after 500ms
        // to let the user see the 100% progress bar.
        console.log('[Upload] All files uploaded successfully, calling onSuccess...');
        onSuccess?.();
        setTimeout(() => { handleClose(); }, 500);
      } else {
        // Name the folders that lost files — "6 failed" out of 500 is useless
        // on its own, because the question is always *which study* is now
        // incomplete and has to be re-sent.
        const affected = Array.from(
          new Set(failedIdx.map((i) => folderOf(fileList[i]) || fileList[i].name))
        ).slice(0, 5);
        message.warning(
          `Uploaded ${successCount} of ${totalFiles} files — ${failCount} failed after retry. ` +
            `Incomplete: ${affected.join(', ')}. Please re-upload the affected folder(s).`,
          10
        );
        if (successCount > 0) {
          onSuccess?.();
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      message.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    pendingRef.current = [];
    clearTimeout(flushTimerRef.current);
    flushTimerRef.current = null;
    setFileList([]);
    setUploadProgress(0);
    setUploadStats(null);
    setSkippedCount(0);
    setScanning(false);
    setFilesDone(0);
    setMode('dicom-only');
    setUploading(false);
    onClose();
  };

  const clearSelection = () => {
    pendingRef.current = [];
    setFileList([]);
    setSkippedCount(0);
  };

  const uploadProps = {
    name: 'file',
    multiple: true,
    // No extension filter in DICOM-only mode: scanner exports are mostly
    // extensionless, and rc-upload would silently discard them before we ever
    // saw them. looksLikeDicom() does the real filtering on content instead.
    accept: mode === 'dicom-only' ? undefined : '.dcm,.dicom,image/*',
    showUploadList: false,
    fileList,
    beforeUpload: (file) => {
      queueFile(file);
      return false;
    },
  };

  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 18 }}>
          <UploadOutlined style={{ color: teal, fontSize: 18 }} />
          Upload New Study
        </span>
      }
      visible={open}
      onCancel={handleClose}
      width={650}
      // An in-flight batch must not be dismissable by a stray click on the
      // mask or the X — abandoning it half way leaves a study missing slices.
      closable={!uploading}
      maskClosable={false}
      keyboard={!uploading}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>,
        <Button
          key="upload"
          type="primary"
          onClick={handleUpload}
          disabled={fileList.length === 0 || scanning}
          loading={uploading}
          icon={uploading ? <UploadOutlined /> : <CheckCircleOutlined />}
          style={{ background: teal, borderColor: teal }}
        >
          {uploading
            ? 'Uploading...'
            : fileList.length > 0
            ? `Upload ${fileList.length.toLocaleString()} file${fileList.length > 1 ? 's' : ''}`
            : 'Upload Study'}
        </Button>,
      ]}
    >
      <p style={{ color: '#6b7280', marginBottom: 24, fontSize: 14 }}>
        Choose how you want to upload your medical imaging study
      </p>

      {/* Mode Selection Cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <button style={modeCardStyle(mode === 'dicom-only')} onClick={() => setMode('dicom-only')}>
          <div style={radioCircleStyle(mode === 'dicom-only')}>
            {mode === 'dicom-only' && <div style={radioInnerStyle} />}
          </div>
          <div>
            <div style={{ fontWeight: 600, color: '#111827', marginBottom: 4 }}>
              DICOM File Only (Recommended)
            </div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              Upload DICOM files and automatically extract patient information from metadata
            </div>
          </div>
        </button>

        <button style={modeCardStyle(mode === 'manual-entry')} onClick={() => setMode('manual-entry')}>
          <div style={radioCircleStyle(mode === 'manual-entry')}>
            {mode === 'manual-entry' && <div style={radioInnerStyle} />}
          </div>
          <div>
            <div style={{ fontWeight: 600, color: '#111827', marginBottom: 4 }}>
              Manual Entry
            </div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              Enter patient and study information manually
            </div>
          </div>
        </button>
      </div>

      {/* DICOM-Only info */}
      {mode === 'dicom-only' && (
        <div style={{
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: 8,
          padding: 16,
          marginBottom: 16,
          fontSize: 14,
          color: '#1e3a5f',
        }}>
          <strong>Auto-extraction enabled:</strong> Patient ID, Name, Age, Gender, Modality, and Body Part
          will be automatically extracted from the DICOM file metadata.
        </div>
      )}

      {/* Manual Entry Fields */}
      {mode === 'manual-entry' && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={fieldLabelStyle}>Patient ID <span style={{ color: '#ef4444' }}>*</span></label>
              <Input placeholder="P12345" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={fieldLabelStyle}>Patient Name <span style={{ color: '#ef4444' }}>*</span></label>
              <Input placeholder="John Doe" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={fieldLabelStyle}>Age</label>
              <Input type="number" placeholder="45" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={fieldLabelStyle}>Gender</label>
              <Select placeholder="Select" style={{ width: '100%' }}>
                <Option value="M">Male</Option>
                <Option value="F">Female</Option>
                <Option value="O">Other</Option>
              </Select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={fieldLabelStyle}>Modality <span style={{ color: '#ef4444' }}>*</span></label>
              <Select placeholder="Select" style={{ width: '100%' }}>
                <Option value="CT">CT Scan</Option>
                <Option value="MRI">MRI</Option>
                <Option value="X-Ray">X-Ray</Option>
                <Option value="US">Ultrasound</Option>
                <Option value="PET">PET Scan</Option>
              </Select>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={fieldLabelStyle}>Body Part</label>
            <Select placeholder="Select body part" style={{ width: '100%' }}>
              <Option value="Head">Head</Option>
              <Option value="Chest">Chest</Option>
              <Option value="Abdomen">Abdomen</Option>
              <Option value="Spine">Spine</Option>
              <Option value="Extremities">Extremities</Option>
            </Select>
          </div>
        </div>
      )}

      {/* File Upload Area.
          openFileDialogOnClick is off so the two explicit buttons below are
          unambiguous — a bare click can only open one kind of OS dialog, and
          "folder or files?" is exactly the choice being made here. Dropping
          still accepts both: with directory set, rc-upload walks folder trees. */}
      <Dragger {...uploadProps} directory openFileDialogOnClick={false}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Drag a patient folder here — or use the buttons below</p>
        <p className="ant-upload-hint" style={{ marginBottom: 12 }}>
          {mode === 'dicom-only'
            ? 'Whole folders are read recursively; every DICOM inside is uploaded'
            : 'DICOM files (.dcm) or medical images'}
        </p>
        <div
          style={{ display: 'flex', gap: 8, justifyContent: 'center' }}
          onClick={(e) => e.stopPropagation()}
        >
          <Upload {...uploadProps} directory>
            <Button icon={<FolderOpenOutlined />} style={{ borderColor: teal, color: teal }}>
              Select Folder
            </Button>
          </Upload>
          <Upload {...uploadProps}>
            <Button icon={<FileAddOutlined />}>Select Files</Button>
          </Upload>
        </div>
      </Dragger>

      {scanning && (
        <div style={{ marginTop: 12, fontSize: 13, color: teal }}>
          <LoadingOutlined style={{ marginRight: 6 }} />
          Reading folder&hellip; {fileList.length > 0 && `${fileList.length} DICOM files found so far`}
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
            <span>
              Uploading{' '}
              <strong style={{ color: '#111827' }}>
                {filesDone.toLocaleString()} of {fileList.length.toLocaleString()}
              </strong>{' '}
              files&hellip;
            </span>
            <span style={{ color: teal, fontWeight: 600 }}>{uploadProgress}%</span>
          </div>
          <Progress
            percent={uploadProgress}
            status="active"
            strokeColor={teal}
            showInfo={false}
          />
          {uploadStats && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 4, color: '#6b7280' }}>
              <span>
                {formatBytes(uploadStats.loaded)} / {formatBytes(uploadStats.totalBytes)}
                {' '}&middot;{' '}
                <strong style={{ color: '#374151' }}>{formatSpeed(uploadStats.speedBps)}</strong>
              </span>
              <span>
                {uploadStats.etaSec > 0 && uploadProgress < 100
                  ? `ETA ${formatDuration(uploadStats.etaSec)}`
                  : 'Finalizing\u2026'}
              </span>
            </div>
          )}
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
            Please keep this window open until the upload finishes.
          </div>
        </div>
      )}

      {fileList.length > 0 && !uploading && (
        <div
          style={{
            marginTop: 16,
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 14px',
              background: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
            }}
          >
            <strong style={{ color: '#111827' }}>
              {fileList.length.toLocaleString()} DICOM file{fileList.length > 1 ? 's' : ''} ready
              {' '}&middot; {formatBytes(summary.totalBytes)}
            </strong>
            <Button type="link" size="small" onClick={clearSelection} style={{ padding: 0 }}>
              Clear
            </Button>
          </div>
          <div style={{ maxHeight: 160, overflowY: 'auto' }}>
            {summary.folders.slice(0, 25).map((f) => (
              <div
                key={f.name}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '6px 14px',
                  fontSize: 13,
                  color: '#374151',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <FolderOpenOutlined style={{ color: '#9ca3af', flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.name}
                  </span>
                </span>
                <span style={{ color: '#6b7280', flexShrink: 0, marginLeft: 12 }}>
                  {f.count.toLocaleString()} files &middot; {formatBytes(f.bytes)}
                </span>
              </div>
            ))}
            {summary.folders.length > 25 && (
              <div style={{ padding: '6px 14px', fontSize: 13, color: '#6b7280' }}>
                + {summary.folders.length - 25} more folders
              </div>
            )}
          </div>
          <div
            style={{
              padding: '8px 14px',
              borderTop: '1px solid #e5e7eb',
              fontSize: 12,
              color: '#6b7280',
            }}
          >
            Studies and series are separated automatically from the DICOM metadata, so one
            upload can contain several patients.
            {skippedCount > 0 && ` ${skippedCount} non-DICOM file${skippedCount > 1 ? 's were' : ' was'} ignored.`}
          </div>
        </div>
      )}
    </Modal>
  );
}
